/**
 * Copyright 2022-2024, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { BaseService, ServiceState } from '../../service';
import { runWithRetry } from '../../utils/executor/backoff_retry_runner';
import { isSuccessStatusCode } from '../../utils/http_request_handler/http_util';
import { ODP_DEFAULT_EVENT_TYPE, ODP_USER_KEY } from '../constant';
import { EVENT_ACTION_INVALID, EVENT_DATA_INVALID, FAILED_TO_SEND_ODP_EVENTS, ODP_EVENTS_SHOULD_HAVE_ATLEAST_ONE_KEY_VALUE, ODP_NOT_INTEGRATED, FAILED_TO_DISPATCH_EVENTS, SERVICE_NOT_RUNNING } from 'error_message';
import { OptimizelyError } from '../../error/optimizly_error';
import { SERVICE_STOPPED_BEFORE_RUNNING } from '../../service';
import { sprintf } from '../../utils/fns';
export const LOGGER_NAME = 'OdpEventManager';
export class DefaultOdpEventManager extends BaseService {
    constructor(config) {
        super(config.startUpLogs);
        this.queue = [];
        this.apiManager = config.apiManager;
        this.batchSize = config.batchSize;
        this.retryConfig = config.retryConfig;
        this.repeater = config.repeater;
        this.repeater.setTask(() => this.flush());
    }
    setLogger(logger) {
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
        this.apiManager.setLogger(logger.child());
    }
    executeDispatch(odpConfig, batch) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.apiManager.sendEvents(odpConfig, batch);
            if (res.statusCode && !isSuccessStatusCode(res.statusCode)) {
                return Promise.reject(new OptimizelyError(FAILED_TO_DISPATCH_EVENTS, res.statusCode));
            }
            return yield Promise.resolve(res);
        });
    }
    flush() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.odpIntegrationConfig || !this.odpIntegrationConfig.integrated) {
                return;
            }
            const odpConfig = this.odpIntegrationConfig.odpConfig;
            const batch = this.queue;
            this.queue = [];
            // as the queue has been emptied, stop repeating flush
            // until more events become available
            this.repeater.reset();
            return runWithRetry(() => this.executeDispatch(odpConfig, batch), this.retryConfig.backoffProvider(), this.retryConfig.maxRetries).result.catch((err) => {
                var _a;
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(FAILED_TO_SEND_ODP_EVENTS, err);
            });
        });
    }
    start() {
        if (!this.isNew()) {
            return;
        }
        super.start();
        if (this.odpIntegrationConfig) {
            this.goToRunningState();
        }
        else {
            this.state = ServiceState.Starting;
        }
    }
    makeDisposable() {
        super.makeDisposable();
        this.retryConfig.maxRetries = Math.min(this.retryConfig.maxRetries, 5);
        this.batchSize = 1;
    }
    updateConfig(odpIntegrationConfig) {
        if (this.isDone()) {
            return;
        }
        if (this.isNew()) {
            this.odpIntegrationConfig = odpIntegrationConfig;
            return;
        }
        if (this.isStarting()) {
            this.odpIntegrationConfig = odpIntegrationConfig;
            this.goToRunningState();
            return;
        }
        // already running, flush the queue using the previous config first before updating the config
        this.flush();
        this.odpIntegrationConfig = odpIntegrationConfig;
    }
    goToRunningState() {
        this.state = ServiceState.Running;
        this.startPromise.resolve();
    }
    flushImmediately() {
        if (!this.isRunning()) {
            return Promise.resolve();
        }
        return this.flush();
    }
    stop() {
        if (this.isDone()) {
            return;
        }
        if (this.isNew()) {
            this.startPromise.reject(new Error(sprintf(SERVICE_STOPPED_BEFORE_RUNNING, 'OdpEventManager')));
        }
        this.flush();
        this.state = ServiceState.Terminated;
        this.stopPromise.resolve();
    }
    sendEvent(event) {
        var _a, _b, _c, _d, _e, _f;
        if (!this.isRunning()) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(SERVICE_NOT_RUNNING, 'OdpEventManager');
            return;
        }
        if (!((_b = this.odpIntegrationConfig) === null || _b === void 0 ? void 0 : _b.integrated)) {
            (_c = this.logger) === null || _c === void 0 ? void 0 : _c.error(ODP_NOT_INTEGRATED);
            return;
        }
        if (event.identifiers.size === 0) {
            (_d = this.logger) === null || _d === void 0 ? void 0 : _d.error(ODP_EVENTS_SHOULD_HAVE_ATLEAST_ONE_KEY_VALUE);
            return;
        }
        if (!this.isDataValid(event.data)) {
            (_e = this.logger) === null || _e === void 0 ? void 0 : _e.error(EVENT_DATA_INVALID);
            return;
        }
        if (!event.action) {
            (_f = this.logger) === null || _f === void 0 ? void 0 : _f.error(EVENT_ACTION_INVALID);
            return;
        }
        if (event.type === '') {
            event.type = ODP_DEFAULT_EVENT_TYPE;
        }
        Array.from(event.identifiers.entries()).forEach(([key, value]) => {
            // Catch for fs-user-id, FS-USER-ID, and FS_USER_ID and assign value to fs_user_id identifier.
            if (ODP_USER_KEY.FS_USER_ID_ALIAS === key.toLowerCase() ||
                ODP_USER_KEY.FS_USER_ID === key.toLowerCase()) {
                event.identifiers.delete(key);
                event.identifiers.set(ODP_USER_KEY.FS_USER_ID, value);
            }
        });
        this.processEvent(event);
    }
    isDataValid(data) {
        const validTypes = ['string', 'number', 'boolean'];
        return Array.from(data.values()).reduce((valid, value) => valid && (value === null || validTypes.includes(typeof value)), true);
    }
    processEvent(event) {
        this.queue.push(event);
        if (this.queue.length === this.batchSize) {
            this.flush();
        }
        else if (!this.repeater.isRunning()) {
            this.repeater.start();
        }
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=odp_event_manager.js.map